import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  Card,
  CardContent,
  Alert,
  LinearProgress,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Print as PrintIcon,
  GetApp as DownloadIcon,
  Refresh as RefreshIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import accountingService from '../../services/accountingService';
import realDataAccountingService from '../../services/realDataAccountingService';
import backendAccountingService from '../../services/backendAccountingService';
import {
  setTrialBalance,
  setLoading,
  setError as setReduxError,
  setPrintData
} from '../../redux/accounting';

const TrialBalance = () => {
  const dispatch = useDispatch();
  const { trialBalance, loading: reduxLoading } = useSelector(state => state.accounting);
  const { admin } = useSelector(state => state.admin);
  const { schooldetails } = useSelector(state => state.schooldetails);
  
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const currentSchool = schooldetails && schooldetails.length > 0 ? schooldetails[0] : null;

  // Get Redux data at component level - using correct structure
  // Handle both data structures: state.students?.data || state.students
  const studentsData = useSelector(state => state.students?.data || state.students || []);
  const classData = useSelector(state => state.class?.data || state.class || []);
  
  const reduxData = {
    students: Array.isArray(studentsData) ? studentsData : [],
    teachers: useSelector(state => state.teachers?.data || state.teachers || []),
    expenses: useSelector(state => state.expenses?.data || state.expenses || []),
    sales: useSelector(state => state.sales?.sales || state.sales || []),
    assets: useSelector(state => state.assets?.assets || state.assets || []),
    class: Array.isArray(classData) ? classData : []
  };

  const loadTrialBalance = useCallback(async () => {
    if (!currentSchool?.schoolID) return;
    
    setLoading(true);
    setError(null);
    dispatch(setLoading({ trialBalance: true }));
    
    try {
      console.log('🔄 Loading trial balance from Redux for school:', currentSchool.schoolID);
      console.log('📊 Redux data available:', {
        students: reduxData.students?.length || 0,
        teachers: reduxData.teachers?.length || 0,
        expenses: reduxData.expenses?.length || 0,
        sales: reduxData.sales?.length || 0,
        assets: reduxData.assets?.length || 0,
        class: reduxData.class?.length || 0
      });
      
      // Generate trial balance from Redux data
      const data = await realDataAccountingService.generateRealTrialBalance(
        currentSchool?.schoolID,
        startDate,
        endDate,
        reduxData
      );
      
      console.log('📊 Redux trial balance data received:', data);
      dispatch(setTrialBalance(data));
    } catch (err) {
      console.error('Redux trial balance error:', err);
      const errorMessage = 'Failed to load trial balance: ' + err.message;
      setError(errorMessage);
      dispatch(setReduxError(errorMessage));
    } finally {
      setLoading(false);
      dispatch(setLoading({ trialBalance: false }));
    }
  }, [currentSchool?.schoolID, startDate, endDate, reduxData, dispatch]);

  useEffect(() => {
    if (currentSchool?.schoolID) {
      loadTrialBalance();
    }
  }, [currentSchool, startDate, endDate, loadTrialBalance]);

  const handleRefresh = () => {
    loadTrialBalance();
  };

  const handlePrint = () => {
    dispatch(setPrintData({ reportType: 'trialBalance', data: trialBalance }));
    // Print logic would be implemented here
  };

  const handleExport = async (format = 'pdf') => {
    try {
      const data = await accountingService.exportReport('trial-balance', trialBalance, format);
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `trial_balance_${startDate}_to_${endDate}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Export error:', err);
    }
  };

  const getAccountTypeColor = (accountType) => {
    const colors = {
      'Asset': '#1976d2',
      'Liability': '#d32f2f',
      'Equity': '#388e3c',
      'Revenue': '#f57c00',
      'Expense': '#9c27b0'
    };
    return colors[accountType] || '#666';
  };

  const calculateTotals = () => {
    if (!trialBalance?.trialBalance) return { totalDebits: 0, totalCredits: 0 };
    
    const totalDebits = trialBalance.trialBalance.reduce((sum, account) => sum + account.debitBalance, 0);
    const totalCredits = trialBalance.trialBalance.reduce((sum, account) => sum + account.creditBalance, 0);
    
    return { totalDebits, totalCredits };
  };

  const { totalDebits, totalCredits } = calculateTotals();

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <LinearProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>Loading trial balance...</Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={handleRefresh}>
            Retry
          </Button>
        }>
          {error}
        </Alert>
      </Container>
    );
  }

  if (!trialBalance && !loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h6">No Trial Balance data available.</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
          Select a date range and click refresh to generate trial balance.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Redux Data Status: Students: {reduxData.students.length}, Teachers: {reduxData.teachers.length}, 
          Expenses: {reduxData.expenses.length}, Sales: {reduxData.sales.length}, Assets: {reduxData.assets.length}
        </Typography>
        <Button variant="contained" onClick={handleRefresh} disabled={loading}>
          {loading ? 'Loading...' : 'Generate Trial Balance'}
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1">
            Trial Balance
          </Typography>
          {trialBalance?.isSampleData && (
            <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
              ⚠️ Showing sample data - No real financial data found in Redux
            </Typography>
          )}
        </Box>
        <Box>
          <Tooltip title="Refresh Data">
            <IconButton onClick={handleRefresh} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  startIcon={<PrintIcon />}
                  onClick={handlePrint}
                  disabled={!trialBalance}
                >
                  Print
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => handleExport('pdf')}
                  disabled={!trialBalance}
                >
                  PDF
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => handleExport('excel')}
                  disabled={!trialBalance}
                >
                  Excel
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Trial Balance Table */}
      {trialBalance && (
        <Paper>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6">
              Trial Balance
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Period: {accountingService.formatDate(startDate)} to {accountingService.formatDate(endDate)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Generated: {accountingService.formatDate(trialBalance.generatedAt)}
            </Typography>
          </Box>
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Account Code</TableCell>
                  <TableCell>Account Name</TableCell>
                  <TableCell>Account Type</TableCell>
                  <TableCell align="right">Debit Balance</TableCell>
                  <TableCell align="right">Credit Balance</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {trialBalance.trialBalance.map((account, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {account.accountCode}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {account.accountName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={account.accountType}
                        size="small"
                        sx={{ 
                          backgroundColor: getAccountTypeColor(account.accountType),
                          color: 'white'
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {account.debitBalance > 0 ? accountingService.formatCurrency(account.debitBalance) : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {account.creditBalance > 0 ? accountingService.formatCurrency(account.creditBalance) : '-'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Totals */}
          <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', backgroundColor: '#f5f5f5' }}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="h6" align="right">
                  Total Debits: {accountingService.formatCurrency(totalDebits)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="h6" align="right">
                  Total Credits: {accountingService.formatCurrency(totalCredits)}
                </Typography>
              </Grid>
            </Grid>
            
            {/* Balance Check */}
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              {Math.abs(totalDebits - totalCredits) < 0.01 ? (
                <Chip
                  label="✓ Trial Balance is Balanced"
                  color="success"
                  icon={<AssessmentIcon />}
                />
              ) : (
                <Chip
                  label={`✗ Trial Balance is NOT Balanced (Difference: ${accountingService.formatCurrency(Math.abs(totalDebits - totalCredits))})`}
                  color="error"
                  icon={<AssessmentIcon />}
                />
              )}
            </Box>
          </Box>
        </Paper>
      )}

      {!trialBalance && !loading && (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <AssessmentIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No trial balance data available
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Select a date range and click refresh to generate trial balance
            </Typography>
          </CardContent>
        </Card>
      )}
    </Container>
  );
};

export default TrialBalance;
